import { pinSize } from '@lessonup/pin-renderer';
import { Box, newBox, newSize, newVector, PinComponent, Vector } from '@lessonup/pins-shared';
import { AnswerByCategoryView, AnswerByCategoryViewAnswer, InitializedPlayerStoreState } from '../../playerStore.types';
import { isAnswerByCategoryEntry } from '../../utils/entries/entryAssertions';
import { createOrderHandler, OrderHandler } from '../../utils/orderHandler/orderHandler';
import { CATEGORY_FALLBACK, keyForAnswer } from '../../utils/pins/answerByCategoryPlayer.utils';
import { normalizeAnswer } from '../../utils/pins/wordCloudPlayer.utils';
import { toInputPinPhase } from '../reducers.utils';
import { CategoryPosition } from './answerByCategoryReducer.model';

interface AnswerInEntry {
  layout?: Box;
  order?: string;
  category?: string;
  normalizedAnswer: string;
}

function findPreviousAnswer(
  text: string,
  category: string,
  previousAnswers: AnswerByCategoryViewAnswer[]
): AnswerByCategoryViewAnswer | undefined {
  return previousAnswers.find((answer) => answer.key === keyForAnswer(text, category));
}

export function updateAnswersInView(
  state: InitializedPlayerStoreState,
  previousView: AnswerByCategoryView | null,
  orderHandler: OrderHandler,
  categories?: CategoryPosition[]
): AnswerByCategoryViewAnswer[] {
  const pinId = state.currentPinId;

  const pinState = pinId ? state.pinState[pinId] : null;
  const previousAnswers = pinState?.answerByCategory?.answers || [];
  const deletedAnswers = pinState?.answerByCategory?.deletedAnswers || [];

  const answersInEntries = new Map<string, AnswerInEntry>();

  state.assignmentEntries.forEach((assignmentEntry) => {
    const studentPinEntry = assignmentEntry.entries[state.currentPinId];
    if (!isAnswerByCategoryEntry(studentPinEntry) || !studentPinEntry?.answer?.words) {
      return;
    }
    studentPinEntry.answer?.words?.forEach((wordAnswer) => {
      const normalized = normalizeAnswer(wordAnswer.word.trim());
      if (!normalized) return;

      if (deletedAnswers.includes(normalized)) {
        return;
      }

      const categoryId = wordAnswer.categoryId || CATEGORY_FALLBACK;
      const answerKey = keyForAnswer(normalized, categoryId);

      if (!answersInEntries.has(answerKey)) {
        const previous = findPreviousAnswer(normalized, categoryId, previousAnswers);
        const finalCategory = previous?.category || categoryId;

        answersInEntries.set(answerKey, {
          layout: previous?.layout,
          order: previous?.order,
          category: finalCategory,
          normalizedAnswer: normalized,
        });
      }
    });
  });

  const newAnswers: AnswerByCategoryViewAnswer[] = [];
  answersInEntries.forEach((data, normalizedAnswer) => {
    if (deletedAnswers.includes(normalizedAnswer)) return;

    const category = data.category || CATEGORY_FALLBACK;
    const layout = data.layout || newBox(findNewPosition(newAnswers, category, categories), newSize(0, 0));
    const order = data.order || orderHandler.generateNext();

    newAnswers.push({
      text: data.normalizedAnswer,
      layout: layout,
      order: order,
      category: category,
      key: keyForAnswer(data.normalizedAnswer, category),
    });
  });

  return newAnswers;
}

const X_DISTANCE_PENALTY = 5;
const MINIMAL_DISTANCE_SCORE = 60;
const MAX_TRIES = 25;

export function findNewPosition(
  answers: AnswerByCategoryViewAnswer[],
  category?: string,
  categories?: CategoryPosition[],
  pinScale: number = 1
): Vector {
  if (category && categories?.length) {
    const targetCategory = categories.find((cat) => cat.id === category);

    if (targetCategory) {
      const answersInCategory = answers.filter((a) => a.category === category);

      const position = findPositionInCategory(targetCategory, answersInCategory, pinScale);

      return position;
    }
  }

  // Fallback to random positioning like wordcloud
  let bestPosition = newVector(0, 0);
  let bestScore = 0;
  let numTry = 0;

  while (numTry < MAX_TRIES) {
    const position = randomPositionOnPin();
    const score = positionScore(answers, position);

    if (score === 1) return position;

    if (bestScore < score) {
      bestScore = score;
      bestPosition = position;
    }

    numTry = numTry + 1;
  }

  return bestPosition;
}

function findPositionInCategory(
  categoryArea: { id: string; layout: { position: Vector; size: { width: number; height: number } } },
  answersInCategory: AnswerByCategoryViewAnswer[],
  pinScale: number
): Vector {
  const MARGIN = 16;

  const maxXRange = Math.max(0, categoryArea.layout.size.width - 2 * MARGIN);
  const maxYRange = Math.max(0, categoryArea.layout.size.height - 2 * MARGIN);

  for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
    const centerX = categoryArea.layout.position.x + MARGIN + Math.random() * maxXRange;
    const centerY = categoryArea.layout.position.y + MARGIN + Math.random() * maxYRange;

    const position = newVector(centerX, centerY);

    const hasOverlap = answersInCategory.some((answer) => {
      if (!answer.layout?.position) return false;
      const distance = Math.hypot(answer.layout.position.x - position.x, answer.layout.position.y - position.y);
      return distance < MINIMAL_DISTANCE_SCORE;
    });

    if (!hasOverlap) {
      return newVector(centerX / pinScale, centerY / pinScale);
    }
  }

  // Fallback: random position within category
  const randomCenterX = categoryArea.layout.position.x + MARGIN + Math.random() * maxXRange;
  const randomCenterY = categoryArea.layout.position.y + MARGIN + Math.random() * maxYRange;

  return newVector(randomCenterX / pinScale, randomCenterY / pinScale);
}

function positionScore(answers: AnswerByCategoryViewAnswer[], positionToCheck: Vector): number {
  if (answers.length === 0) return 1;

  const distances = answers.map((answer) => distanceScore(answer, positionToCheck));
  const closestDistance = Math.min(...distances);

  const score = Math.min(closestDistance / MINIMAL_DISTANCE_SCORE, 1);
  return score;
}

function distanceScore(answer: AnswerByCategoryViewAnswer, positionToCheck: Vector): number {
  if (!answer.layout?.position) {
    return Infinity;
  }
  return Math.hypot(
    (answer.layout.position.x - positionToCheck.x) / X_DISTANCE_PENALTY,
    answer.layout.position.y - positionToCheck.y
  );
}

function randomPositionOnPin(): Vector {
  return newVector(Math.random() * pinSize.width, Math.random() * pinSize.height);
}

export function initializeAnswerByCategoryPinState(state: InitializedPlayerStoreState, pinId: string) {
  if (!state.pinState[pinId]) {
    state.pinState[pinId] = {
      view: null,
      componentState: {},
    };
  }

  if (!state.pinState[pinId].answerByCategory) {
    state.pinState[pinId].answerByCategory = {
      answers: [],
      revealedAnswers: [],
      deletedAnswers: [],
      lastOrder: '',
    };
  }

  return state.pinState[pinId];
}

export function syncAnswerByCategoryViewWithPinState(
  state: InitializedPlayerStoreState,
  pinId: string
): AnswerByCategoryView {
  if (!state.pinState[pinId] || !state.pinState[pinId].answerByCategory || !state.currentView) {
    return { type: 'ANSWER_BY_CATEGORY', phase: 'input' };
  }

  return {
    type: 'ANSWER_BY_CATEGORY',
    phase: toInputPinPhase(state.currentView.phase),
  };
}

export function updateAnswerByCategoryAnswerOrder(
  answers: AnswerByCategoryViewAnswer[],
  answerText: string,
  lastOrder: string
) {
  let newLastOrder = lastOrder;

  try {
    const nextOrder = createOrderHandler({ start: lastOrder }).generateNext();
    newLastOrder = nextOrder;
  } catch {
    const orderHandler = createOrderHandler();
    newLastOrder = orderHandler.generateNext();
  }

  const updatedAnswers = answers.map((answer) => {
    if (answer.key === keyForAnswer(answerText, answer.category)) {
      return {
        ...answer,
        order: newLastOrder,
      };
    }
    return answer;
  });

  return { updatedAnswers, newLastOrder };
}

export function extractCategories(pin: { pinComponents?: PinComponent[] } | null | undefined): CategoryPosition[] {
  const categoryAreaComponent = pin?.pinComponents?.find(
    (component): component is PinComponent & { type: 'CATEGORY_AREA'; layout: Box } =>
      component.type === 'CATEGORY_AREA' && component.layout.type === 'BOX'
  );

  if (!categoryAreaComponent || !('categories' in categoryAreaComponent.settings)) {
    return [];
  }

  const containerLayout = {
    position: categoryAreaComponent.layout.position,
    size: categoryAreaComponent.layout.size,
  };

  const settings = categoryAreaComponent.settings;
  const categories = settings.categories || [];
  const layoutMode = settings.layoutMode || 'AUTO';

  return categories.map((category) => ({
    id: category.id,
    layout: {
      ...category.layout,
      position:
        layoutMode === 'AUTO'
          ? calculateAutoLayoutPosition(category, categories, containerLayout)
          : calculateCustomLayoutPosition(category, containerLayout),
      size:
        layoutMode === 'AUTO'
          ? calculateAutoLayoutSize(categories, containerLayout)
          : calculateCustomLayoutSize(category, containerLayout),
    },
  }));
}

const MAX_CATEGORIES_PER_ROW = 4;

function calculateAutoLayoutPosition(
  category: CategoryPosition,
  allCategories: CategoryPosition[],
  containerLayout: { position: { x: number; y: number }; size: { width: number; height: number } }
) {
  const categoryIndex = allCategories.findIndex((c) => c.id === category.id);
  const totalCategories = allCategories.length;

  const padding = 16;
  const gap = 10;
  const availableWidth = containerLayout.size.width - padding;
  const availableHeight = containerLayout.size.height - padding;

  // Force maximum of 4 columns to match the CSS layout
  const categoriesPerRow = Math.min(MAX_CATEGORIES_PER_ROW, totalCategories);
  const totalRows = Math.ceil(totalCategories / categoriesPerRow);

  const cellWidth = (availableWidth - (categoriesPerRow - 1) * gap) / categoriesPerRow;
  const cellHeight = (availableHeight - (totalRows - 1) * gap) / totalRows;

  const row = Math.floor(categoryIndex / categoriesPerRow);
  const col = categoryIndex % categoriesPerRow;

  return {
    x: containerLayout.position.x + padding / 2 + col * (cellWidth + gap),
    y: containerLayout.position.y + padding / 2 + row * (cellHeight + gap),
  };
}

function calculateAutoLayoutSize(
  allCategories: CategoryPosition[],
  containerLayout: { position: { x: number; y: number }; size: { width: number; height: number } }
) {
  const totalCategories = allCategories.length;

  const padding = 16;
  const gap = 10;
  const availableWidth = containerLayout.size.width - padding;
  const availableHeight = containerLayout.size.height - padding;

  const categoriesPerRow = Math.min(MAX_CATEGORIES_PER_ROW, totalCategories);
  const totalRows = Math.ceil(totalCategories / categoriesPerRow);

  const cellWidth = (availableWidth - (categoriesPerRow - 1) * gap) / categoriesPerRow;
  const cellHeight = (availableHeight - (totalRows - 1) * gap) / totalRows;

  return {
    width: Math.max(50, cellWidth),
    height: Math.max(30, cellHeight),
  };
}

function calculateCustomLayoutPosition(
  category: { layout: { position: { x: number; y: number } } },
  containerLayout: { position: { x: number; y: number }; size: { width: number; height: number } }
) {
  return {
    x: containerLayout.position.x + (category.layout.position.x / 100) * containerLayout.size.width,
    y: containerLayout.position.y + (category.layout.position.y / 100) * containerLayout.size.height,
  };
}

function calculateCustomLayoutSize(
  category: { layout: { size: { width: number; height: number } } },
  containerLayout: { size: { width: number; height: number } }
) {
  return {
    width: (category.layout.size.width / 100) * containerLayout.size.width,
    height: (category.layout.size.height / 100) * containerLayout.size.height,
  };
}

/**
 * Creates order handler with fallback logic
 */
export function createAnswerByCategoryOrderHandler(lastOrder?: string) {
  try {
    const startOrder = lastOrder || '';
    return createOrderHandler({
      start: startOrder.length > 0 ? startOrder : 'a0',
    });
  } catch {
    return createOrderHandler();
  }
}

/**
 * Updates answer by category pin state with new data
 */
export function updateAnswerByCategoryPinState(
  state: InitializedPlayerStoreState,
  pinId: string,
  view: AnswerByCategoryView,
  orderHandler: ReturnType<typeof createOrderHandler>,
  categories: CategoryPosition[]
) {
  const pinState = initializeAnswerByCategoryPinState(state, pinId);
  const answerByCategoryData = pinState.answerByCategory;

  const answers = updateAnswersInView(state, view, orderHandler, categories);
  const deletedAnswers = answerByCategoryData?.deletedAnswers || [];
  const revealedAnswers = answerByCategoryData?.revealedAnswers || [];
  const lastOrder = orderHandler.getOrder();

  state.pinState[pinId].answerByCategory = {
    answers,
    revealedAnswers,
    deletedAnswers,
    lastOrder,
  };

  return { answers, deletedAnswers, revealedAnswers, lastOrder };
}
